<template>
  <div class="max-w-[720px] w-full px-4 sm:px-0 sm:mr-5">
    <h2 class="w-full text-center mb-8">
      {{ $t('messages.menu.passkey') }}
    </h2>
    <div class="list--account flex flex-col border border-gray-200 bg-white rounded-lg sm:ml-10">
      <!-- Set up passkeys section -->
      <div class="p-6 border-b border-gray-200">
        <div class="flex justify-between items-center">
          <div>
            <h3 class="m-0">
              {{ $t('messages.passkey.sub_title') }}
            </h3>
            <p class="mt-3 mb-3 b1">
              {{ $t('messages.passkey.description') }}
            </p>
          </div>
          <button
            class="cursor-pointer text-zyxel-green-700 font-bold ml-2"
            :disabled="isAddPasskeyDisabled || isDemoAccount"
            :class="{ 'cursor-not-allowed text-zyxel-gray-800': isAddPasskeyDisabled || isDemoAccount }"
            @click="handleAddPasskey"
          >
            {{ $t('messages.passkey.add_button') }}
          </button>
        </div>

        <!-- Passwordless Recommendation -->
        <div
          v-if="user.passwordless_status === 'eligible'"
          class="mt-4"
        >
          <div class="flex items-start gap-2">
            <div class="text-gray-700">
              <p class="mb-2">
                {{ $t('messages.passwordless.recommendation') }}
              </p>
              <button
                class="text-zyxel-green-700 font-bold cursor-pointer hover:underline"
                @click="handleGoPasswordless"
              >
                {{ $t('messages.passwordless.go_passwordless') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Passkey List -->
      <div
        v-for="record in passkeyList"
        :key="record.created_at"
        class="flex items-start p-6"
      >
        <div class="flex items-start mr-4 mt-2">
          <img
            class="w-12 h-12"
            :src="getPasskeyIcon(record.vendor_id)"
            :alt="getPasskeyName(record.vendor_id)"
          >
        </div>
        <div class="caption w-full">
          <div>
            <strong>{{ record.display_name }}</strong>
          </div>
          <div class="b2 flex mt-1 md:items-center">
            {{ $t('messages.passkey.created') }}: {{ formatDate(new Date(record.created_at + 'Z')) }}
          </div>
          <div class="b2 flex mt-1 md:items-center">
            {{ $t('messages.passkey.last_used') }}: {{ record.last_used ? formatDateTime(record.last_used) : $t('messages.passkey.not_yet_used') }}
          </div>
        </div>
        <div class="flex items-center self-center mr-2">
          <button
            class="w-6 h-6 cursor-pointer mr-4"
            :disabled="isDemoAccount"
            :class="{ 'cursor-not-allowed opacity-50': isDemoAccount }"
            aria-label="Edit this passkey"
            @click="openEditKeyDialog(record)"
          >
            <img
              class="w-6 h-6"
              src="@/assets/svg/icon_24px_edit.svg"
              alt="edit"
            >
          </button>
          <button
            class="w-6 h-6"
            :disabled="isDemoAccount || isLastPasskeyAndPasswordless"
            :class="(isDemoAccount || isLastPasskeyAndPasswordless) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'"
            aria-label="Delete this passkey"
            @click="openDeleteKeyDialog(record)"
          >
            <img
              class="w-6 h-6"
              src="@/assets/svg/icon_24px_trash_can.svg"
              alt="delete"
            >
          </button>
        </div>
      </div>

      <!-- Mobile Close Button -->
      <div
        v-if="isMobile"
        class="modal-footer flex-col sm:hidden p-6"
      >
        <button
          class="button-cancel w-full mb-4"
          @click="handleClose"
        >
          {{ $t("messages.button.close") }}
        </button>
      </div>
    </div>
    <Loading
      v-model:active="apiLoading"
      color="#3C9F00"
      loader="spinner"
      background-color="#FFFFFF"
      :is-full-page="true"
    />

    <DeletePasskeyDialog
      :is-visible="showDeleteDialog"
      :record="selectedPasskey"
      @confirm="handleDeleteKeyConfirm"
      @cancel="handleDeleteKeyCancel"
    />

    <EditPasskeyDialog
      :is-open="isEditKeyDialogOpen"
      :passkey="selectedPasskey"
      @close="closeEditKeyDialog"
      @save="handleEditPasskey"
    />

    <SetPasskeyNameDialog
      :is-set-passkey-open="isSetPasskeyNameDialogOpen"
      :passkey="newPasskeyData"
      @save="handleSetPasskeyName"
    />

    <GeneralDialog
      :is-show="isGeneralDialogShow"
      :title="generalDialogProps.title"
      :description="generalDialogProps.description"
      @close="closeGeneralDialog"
    />

    <PasswordlessConfirmModal
      :is-visible="showPasswordlessModal"
      @close-modal="showPasswordlessModal = false"
      @confirm="confirmEnablePasswordless"
    />

    <GeneralDialog
      :is-show="showSuccessModal"
      :title="$t('messages.passwordless.success_modal.title')"
      :description="$t('messages.passwordless.success_modal.description')"
      @close="showSuccessModal = false"
    />
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth';
import axios from 'axios';
import { apiBaseUrl } from '@/config';
import { base64urlDecode, arrayBufferToBase64url } from '@/utils/passkeyUtils';
import EditPasskeyDialog from './EditPasskeyDialog.vue';
import DeletePasskeyDialog from '@/components/DeletePasskeyDialog.vue'
import SetPasskeyNameDialog from './SetPasskeyNameDialog.vue';
import GeneralDialog from "@/components/GeneralDialog.vue";
import PasswordlessConfirmModal from '@/components/PasswordlessConfirmModal.vue';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import { logger } from '@/utils/logger';

export default {
    name: 'Passkey',
    components: {
        EditPasskeyDialog,
        DeletePasskeyDialog,
        SetPasskeyNameDialog,
        GeneralDialog,
        PasswordlessConfirmModal,
        Loading
    },
    data() {
        return {
            passkeyList: [],
            isLoading: false,
            apiLoading: false, // loading 元件用
            showDeleteDialog: false,
            selectedRecord: null,
            isEditKeyDialogOpen: false,
            selectedPasskey: null,
            isSetPasskeyNameDialogOpen: false,
            newPasskeyData: {},
            isGeneralDialogShow: false,
            generalDialogProps: {
                title: "",
                description: ""
            },
            isAddPasskeyDisabled: false,
            addPasskeyClickTime: 0,
            showPasswordlessModal: false,
            showSuccessModal: false
        };
    },
    computed: {
        user() {
            const authStore = useAuthStore();
            return authStore.current_user || {};
        },
        isDemoAccount() {
            const authStore = useAuthStore();
            return authStore.isDemoAccount;
        },
        isPasskeyLimitReached() {
            return this.passkeyList.length >= 5;
        },
        isLastPasskeyAndPasswordless() {
            // Passwordless 用戶且只剩一把 passkey 時不能刪除（否則無法登入）
            return this.user.passwordless_status === 'completed' && this.passkeyList.length === 1;
        },
        isMobile() {
            return /Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        }
    },
    async created() {
        const authStore = useAuthStore();
        await authStore.fetchCurrentUser(); // Refresh user data to get latest passwordless_status
        await this.loadPasskey();
    },
    methods: {
        formatDate(date) {
            return date.toISOString().split('T')[0];
        },
        formatDateTime(datetime) {
            const date = new Date(datetime + 'Z');

            const offset = -date.getTimezoneOffset();
            const offsetHours = Math.floor(Math.abs(offset) / 60);
            const offsetMinutes = Math.abs(offset) % 60;
            const timezoneFormatted = `UTC${offset >= 0 ? '+' : '-'}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;

            const dateStr = date.toISOString().split('T')[0];
            const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

            return `${dateStr} ${timeStr} (${timezoneFormatted})`;
        },
        getVendorIconMap() {
            return {
                1: { icon: require('@/assets/svg/icon_48px_windows_hello.svg'), name: 'Windows Hello' },
                2: { icon: require('@/assets/svg/icon_48px_icloud_keychain.svg'), name: 'iCloud Keychain' },
                3: { icon: require('@/assets/svg/icon_48px_chrome.svg'), name: 'Chrome' },
                4: { icon: require('@/assets/svg/icon_48px_google_password_manager.svg'), name: 'Google Password Manager' },
                5: { icon: require('@/assets/svg/icon_48px_thales.svg'), name: 'Thales' },
                6: { icon: require('@/assets/svg/icon_48px_logmeonce.svg'), name: 'LogMeOnce' },
                7: { icon: require('@/assets/svg/icon_48px_idmelon.svg'), name: 'IDmelon' },
                8: { icon: require('@/assets/svg/icon_48px_proton_pass.svg'), name: 'Proton Pass' },
                9: { icon: require('@/assets/svg/icon_48px_dashlane.svg'), name: 'Dashlane' },
                10: { icon: require('@/assets/svg/icon_48px_samsung_pass.svg'), name: 'Samsung Pass' },
                11: { icon: require('@/assets/svg/icon_48px_edge.svg'), name: 'Edge' },
                12: { icon: require('@/assets/svg/icon_48px_sesame.svg'), name: 'Sésame' },
                13: { icon: require('@/assets/svg/icon_48px_kaspersky_password_manager.svg'), name: 'Kaspersky Password Manager' },
                14: { icon: require('@/assets/svg/icon_48px_zohovault.svg'), name: 'Zoho Vault' },
                15: { icon: require('@/assets/svg/icon_48px_lastpass.svg'), name: 'LastPass' },
                16: { icon: require('@/assets/svg/icon_48px_nordpass.svg'), name: 'NordPass' },
                17: { icon: require('@/assets/svg/icon_48px_1password.svg'), name: '1Password' },
                18: { icon: require('@/assets/svg/icon_48px_iPasswords.svg'), name: 'iPasswords' },
                19: { icon: require('@/assets/svg/icon_48px_toothpic_passkey_provider.svg'), name: 'ToothPic Passkey Provider' },
                21: { icon: require('@/assets/svg/icon_48px_pwsafe.svg'), name: 'pwSafe' },
                22: { icon: require('@/assets/svg/icon_48px_bitwarden.svg'), name: 'Bitwarden' },
                23: { icon: require('@/assets/svg/icon_48px_keeper.svg'), name: 'Keeper' },
                24: { icon: require('@/assets/svg/icon_48px_devolutions.svg'), name: 'Devolutions' },
                25: { icon: require('@/assets/svg/icon_48px_enpass.svg'), name: 'Enpass' },
                26: { icon: require('@/assets/svg/icon_48px_keepassxc.svg'), name: 'KeePassXC' },
                999: { icon: require('@/assets/svg/icon_48px_cloud_authenticator.svg'), name: 'General Passkey' },
                1000: { icon: require('@/assets/svg/icon_48px_security_key.svg'), name: 'Security Key' }
            };
        },
        getPasskeyIcon(vendorId) {
            const vendorIconMap = this.getVendorIconMap();
            if (vendorId && vendorIconMap[vendorId]) {
                return vendorIconMap[vendorId].icon;
            } else {
                return require('@/assets/svg/icon_48px_cloud_authenticator.svg');
            }
        },
        getPasskeyName(vendorId) {
            const vendorIconMap = this.getVendorIconMap();
            if (vendorId && vendorIconMap[vendorId]) {
                return vendorIconMap[vendorId].name;
            } else {
                return 'Unknown Passkey';
            }
        },
        openEditKeyDialog(record) {
            this.selectedPasskey = record;
            this.isEditKeyDialogOpen = true;
        },
        closeEditKeyDialog() {
            this.isEditKeyDialogOpen = false;
            this.selectedPasskey = null;
        },
        async handleSetPasskeyName(updatedPasskey) {
            this.apiLoading = true;
            try {
                await axios.patch(`${apiBaseUrl}/api/passkey/${updatedPasskey.passkey_id}`, {
                    display_name: updatedPasskey.display_name
                });
                const authStore = useAuthStore();
                await authStore.fetchCurrentUser(); // Refresh user data to update passwordless_status
                await this.loadPasskey(); // 立即更新 passkey 列表，確保 UI 狀態同步
                this.isSetPasskeyNameDialogOpen = false;
                this.newPasskeyData = null;
                this.showSuccessDialog();
            } catch (error) {
                logger.error('Error updating passkey name:', error);
                this.showAddFailDialog();
            } finally {
                this.apiLoading = false;
            }
        },
        async handleEditPasskey(updatedPasskey) {
            this.apiLoading = true;
            try {
                await axios.patch(`${apiBaseUrl}/api/passkey/${updatedPasskey.passkey_id}`, {
                    display_name: updatedPasskey.display_name
                });
                this.closeEditKeyDialog();
                await this.loadPasskey(); // Reload passkeys after successful update
            } catch (error) {
                logger.error('Error updating passkey:', error);
                // TODO: Show error message to user
            } finally {
                this.apiLoading = false;
            }
        },
        async handleAddPasskey() {
            // 防重複點擊機制
            const now = Date.now();
            if (this.isAddPasskeyDisabled || (now - this.addPasskeyClickTime < 3000)) {
                return;
            }
            this.addPasskeyClickTime = now;
            this.isAddPasskeyDisabled = true;

            // 檢查passkey數量限制
            if (this.isPasskeyLimitReached) {
                this.showPasskeyLimitDialog();
                this.isAddPasskeyDisabled = false;
                return;
            }

            this.apiLoading = true;
            try {
                const response = await axios.get(`${apiBaseUrl}/api/passkey/add`);
                const options = response.data;
                options.user.id = base64urlDecode(options.user.id);
                options.challenge = base64urlDecode(options.challenge);
                if (options.excludeCredentials) {
                    options.excludeCredentials.forEach(cred => {
                        cred.id = base64urlDecode(cred.id);
                    });
                }
                // Remove empty authenticatorAttachment
                if (options.authenticatorSelection?.authenticatorAttachment === "") {
                    delete options.authenticatorSelection.authenticatorAttachment;
                }
                options.attestation = "direct";

                const credential = await navigator.credentials.create({ publicKey: options });
                if (!credential || !credential.id) {
                    logger.error('Invalid credential:', credential);
                    this.apiLoading = false;
                    return;
                }
                const verificationPayload = {
                    id: credential.id,
                    rawId: arrayBufferToBase64url(credential.rawId),
                    response: {
                        attestationObject: arrayBufferToBase64url(credential.response.attestationObject),
                        clientDataJSON: arrayBufferToBase64url(credential.response.clientDataJSON),
                        transports: credential.response.getTransports?.() ?? []
                    },
                    type: credential.type,
                    clientExtensionResults: credential.clientExtensionResults ?? {},
                    authenticatorAttachment: credential.authenticatorAttachment,
                };
                const verify_time = new Date().toString();
                const payload = {
                    verify_time,
                    credential: verificationPayload
                };
                const verificationResponse = await axios.post(`${apiBaseUrl}/api/passkey/verify`, payload);
                const newPasskeyData = verificationResponse.data;
                // 顯示設定 passkey 名稱的對話框
                this.newPasskeyData = newPasskeyData;
                this.isSetPasskeyNameDialogOpen = true;
            } catch (error) {
                logger.error('Error passkey registration options or creating credential:', error);
                const error_code = error?.response?.data?.detail?.error_code;
                if (error_code === 94522) { // Device no UV
                    this.showAddFailNoUVDialog();
                } else {
                    this.showAddFailDialog();
                }
            } finally {
                this.apiLoading = false;
                // 設定3秒後重新啟用按鈕
                setTimeout(() => {
                    this.isAddPasskeyDisabled = false;
                }, 3000);
            }
        },
        async loadPasskey() {
            this.isLoading = true;
            try {
                const response = await axios.get(`${apiBaseUrl}/api/users/me/passkey_setting`);
                const newPasskey = response.data.data;
                this.passkeyList = newPasskey;
            } catch (error) {
                logger.error('API error:', error);
            } finally {
                this.isLoading = false;
            }
        },
        async openDeleteKeyDialog(record) {
            this.selectedPasskey = record;
            this.showDeleteDialog = true;
        },
        async handleDeleteKeyConfirm(record) {
            this.apiLoading = true;
            try {
                const delete_time = new Date().toString();
                const deleteResponse = await axios.request({
                    method: 'delete',
                    url: `${apiBaseUrl}/api/passkey/${record.passkey_id}`,
                    data: {
                        delete_time
                    }
                });
                if (deleteResponse.data.success) {
                    const authStore = useAuthStore();
                    await authStore.fetchCurrentUser(); // Refresh user data to update passwordless_status
                    await this.loadPasskey(); // 立即更新 passkey 列表，確保刪除按鈕狀態即時更新
                    this.showDeleteDialog = false;
                    this.showDeleteSuccessDialog();
                }
                return deleteResponse.data
            } catch (error) {
                logger.error('Error delete passkey:', error);
                this.showDeleteDialog = false;
                this.showDeleteFailDialog();
            } finally {
                this.apiLoading = false;
            }
        },
        handleDeleteKeyCancel() {
            this.selectedPasskey = null;
            this.showDeleteDialog = false;
        },
        showSuccessDialog() {
            this.generalDialogProps.title = this.$t('messages.added_passkey_modal.title');
            this.generalDialogProps.description = this.$t('messages.added_passkey_modal.description');
            this.isGeneralDialogShow = true;
        },
        showAddFailDialog() {
            this.generalDialogProps.title = this.$t('messages.add_passkey_fail_modal.title');
            this.generalDialogProps.description = this.$t('messages.add_passkey_fail_modal.description');
            this.isGeneralDialogShow = true;
        },
        showAddFailNoUVDialog() {
            this.generalDialogProps.title = this.$t('messages.add_passkey_fail_modal.title');
            this.generalDialogProps.description = this.$t('messages.error_msg.device_no_uv');
            this.isGeneralDialogShow = true;
        },
        showDeleteSuccessDialog() {
            this.generalDialogProps.title = this.$t('messages.deleted_passkey_modal.title');
            this.generalDialogProps.description = this.$t('messages.deleted_passkey_modal.description');
            this.isGeneralDialogShow = true;
        },
        showDeleteFailDialog() {
            this.generalDialogProps.title = this.$t('messages.delete_passkey_fail_modal.title');
            this.generalDialogProps.description = this.$t('messages.delete_passkey_fail_modal.description');
            this.isGeneralDialogShow = true;
        },
        showPasskeyLimitDialog() {
            this.generalDialogProps.title = this.$t('messages.passkey_limit_modal.title');
            this.generalDialogProps.description = this.$t('messages.passkey_limit_modal.description');
            this.isGeneralDialogShow = true;
        },
        async closeGeneralDialog() {
            this.isGeneralDialogShow = false;
            await this.loadPasskey();
        },
        handleGoPasswordless() {
            this.showPasswordlessModal = true;
        },
        handleClose() {
            window.close();
            setTimeout(() => {
                if (window.history.state?.back) {
                    this.$router.go(-1);
                } else {
                    this.$router.push('/user');
                }
            }, 200);
        },
        async confirmEnablePasswordless() {
            const authStore = useAuthStore();

            try {
                const result = await authStore.enablePasswordless();

                if (result.success) {
                    this.showPasswordlessModal = false;
                    this.showSuccessModal = true;
                    // User data will be automatically refreshed by authStore.enablePasswordless()
                } else {
                    this.showPasswordlessModal = false;
                    // Show error notification
                    alert(this.$t('messages.passwordless.error_message'));
                    logger.error('Failed to enable passwordless:', result.error);
                }
            } catch (error) {
                this.showPasswordlessModal = false;
                alert(this.$t('messages.passwordless.error_message'));
                logger.error('Error enabling passwordless:', error);
            }
        }
    }
}
</script>
